if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        const productPrev = this.querySelector('#Product-Prev');
        const productNext = this.querySelector('#Product-Next');

        if (productPrev && productNext) {
          productPrev.addEventListener('click', this.handleProductPrev.bind(this));
          productNext.addEventListener('click', this.handleProductNext.bind(this));
        }

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.ignoreSlideChanged = false;
        this.isTransitioning = false;

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (this.ignoreSlideChanged) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        const mediaId = thumbnail.getAttribute('data-target')
        this.setActiveMedia(mediaId);
      }

      async setActiveMedia(mediaId, prepend) {
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        this.ignoreSlideChanged = true;

        // Find target media
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

        // SAFETY CHECK – if target media missing, fall back to first media so the gallery is never blank.
        if (!activeMedia) {
          console.warn(`No media found for ID: ${mediaId}, falling back to first media`);
          activeMedia = this.elements.viewer.querySelector('[data-media-id]');
          if (!activeMedia) {
            this.isTransitioning = false;
            this.ignoreSlideChanged = false;
            return;
          }
        }

        // Remove existing active class
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active');
        });

        // Add active class to selected
        activeMedia.classList.add('is-active');

        window.lastViewedMediaId = mediaId;

        // Move order if prepend is requested
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        // Update thumbnail state
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            if (activeThumbnail.dataset.mediaPosition) {
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }
        }

        setTimeout(() => {
          this.ignoreSlideChanged = false;
          this.isTransitioning = false;
        }, 500);
      }


      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10))
          return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      handleProductPrev() {
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        const mediaIds = Array.from(mediaItems).map((element) => element.dataset.mediaId);

        if (mediaIds.length === 0) {
          console.error("No mediaIds found in elements with class 'product__media-item'");
          return;
        }

        const currentIndex = mediaIds.indexOf(this.getActiveMediaId());
        const prevIndex = (currentIndex - 1 + mediaIds.length) % mediaIds.length;
        const prevMediaId = mediaIds[prevIndex];

        this.setActiveMedia(prevMediaId, false);
      }

      handleProductNext() {
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        const mediaIds = Array.from(mediaItems).map((element) => element.dataset.mediaId);

        if (mediaIds.length === 0) {
          console.error("No mediaIds found in elements with class 'product__media-item'");
          return;
        }

        const currentIndex = mediaIds.indexOf(this.getActiveMediaId());
        const nextIndex = (currentIndex + 1) % mediaIds.length;
        const nextMediaId = mediaIds[nextIndex];

        this.setActiveMedia(nextMediaId, false);
      }

      getActiveMediaId() {
        const activeMedia = this.elements.viewer.querySelector('.is-active');
        return activeMedia ? activeMedia.dataset.mediaId : null;
      }
    }
  );
}


